import React, { useState, useCallback, useRef, useEffect } from 'react';
import { IconClose } from '../../icons';
import { Upload } from 'lucide-react';
import ModalDefault from '../../ModalDefault';
import { ModalContactHead, ModalcontactMain } from '../../SearchProfessionalProposal/styles';
import { comum } from '../../../styles/Themes/light';
import { PendenciaKanban, StatusKanbanType, ResultadoType } from './types';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../../../config/toastConfig';
import { Badge } from './styles';
import { Trash2, FileText } from 'lucide-react';
import styled from 'styled-components';
import { 
    CloseButtonHeader, 
    ModalSection, 
    Label, 
    Value, 
    TextArea, 
    ButtonGroup, 
    Button 
} from './modalStyles';
import { 
    formatTipo, 
    formatCurrency, 
    isTipoFinanceiro, 
    isTipoDocumentacao, 
    isTipoInformativa 
} from './utils';
import { truncarNomeArquivo } from '../../../utility/byteSizeUtils';

interface ModalConcluirPendenciaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    pendencia: PendenciaKanban | null;
    statusAnterior: StatusKanbanType | null;
    onConcluida: () => void;
    onCancelar: () => void;
}

interface ArquivoUpload {
    id_proposta_arquivo?: number;
    caminho: string;
    arquivo_final: 'sim' | 'não';
    nome?: string;
}

const ModalContentCompact = styled(ModalcontactMain)`
    gap: 16px;
    padding: 20px 10px;
`;

const UploadArea = styled.div<{ isDragging?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    justify-content: center;
    border: 2px dotted ${({ theme, isDragging }) => isDragging ? theme.colors.primaryRed400 : theme.colors.interfaceGray200};
    border-radius: 6px;
    padding: 16px;
    transition: all 0.3s ease;
    background: ${({ theme, isDragging }) => isDragging ? (theme.colors.primaryRed50 || 'rgba(239, 68, 68, 0.08)') : 'transparent'};

    &:hover {
        border-color: ${({ theme, isDragging }) => isDragging ? undefined : theme.colors.interfaceText200};
    }

    input[type="file"] {
        display: none;
    }

    .upload-container {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${({ theme }) => theme.colors.interfacePure100};
        flex-shrink: 0;
        svg {
            width: 18px;
            height: 18px;
            path {
                color: ${({ theme }) => theme.colors.interfaceGray200};
            }
        }
    }

    .texto-anexos {
        display: flex;
        flex-direction: column;
        gap: 2px;
        align-items: center;
        text-align: center;
        .principal {
            margin: 0;
            font-size: 13px;
            font-weight: 600;
            color: ${({ theme }) => theme.colors.c000000};
            line-height: 1.3;
        }
        .secundario {
            margin: 0;
            font-size: 11px;
            font-weight: 400;
            color: ${({ theme }) => theme.colors.interfaceGray400};
            line-height: 1.3;
        }
        .limite {
            margin: 0;
            font-size: 10px;
            color: ${({ theme }) => theme.colors.interfaceGray400};
            line-height: 1.3;
        }
    }

    .input-container > button {
        font-size: 11px;
        padding: 8px 12px;
        height: auto;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: ${(props: any) => props.theme.colors.interfacePure0};
        border: 2px solid ${({ theme }) => theme.colors.interfacePure100};
        color: ${({ theme }) => theme.colors.c000000};
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        white-space: nowrap;
        &:hover {
            background: ${({ theme }) => theme.colors.interfacePure100};
        }
        svg {
            width: 14px;
            height: 14px;
        }
    }
`;

const ArquivoList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
`;

const ArquivoItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: ${({ theme }) => theme.colors.interfacePure100};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.interfaceText100};

    .file-info {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 0;
        
        span {
            font-size: 14px;
            color: ${({ theme }) => theme.colors.interfaceText400};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
        }
    }

    .remove-btn {
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
        
        &:hover {
            background: ${({ theme }) => theme.colors.interfaceText100};
        }
    }
`;

const JustificativaContainer = styled.div`
    margin-top: 16px;
`;

const truncarNomeEmMensagemErro = (mensagem: string): string => {
    const padrao = /"([^"]+)"|'([^']+)'/g;
    return mensagem.replace(padrao, (match, nome1, nome2) => {
        const nomeCompleto = nome1 || nome2;
        if (nomeCompleto && nomeCompleto.length > 10) {
            const nomeTruncado = truncarNomeArquivo(nomeCompleto, 40);
            const aspas = match.startsWith('"') ? '"' : "'";
            return `${aspas}${nomeTruncado}${aspas}`;
        }
        return match;
    });
};

const ModalConcluirPendencia: React.FC<ModalConcluirPendenciaProps> = ({
    modalAtivo,
    setModalAtivo,
    pendencia,
    statusAnterior,
    onConcluida,
    onCancelar
}) => {
    const [resposta, setResposta] = useState<string>('');
    const [resultado, setResultado] = useState<'Aceita' | 'Recusada' | null>(null);
    const [justificativa, setJustificativa] = useState<string>('');
    const [arquivos, setArquivos] = useState<ArquivoUpload[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isConcluindo, setIsConcluindo] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isMountedRef = useRef<boolean>(true);

    const isFinanceira = pendencia ? isTipoFinanceiro(pendencia.tipo) : false;
    const isDocumentacao = pendencia ? isTipoDocumentacao(pendencia.tipo) : false;
    const isInformativa = pendencia ? isTipoInformativa(pendencia.tipo) : false;

    const handleFileUpload = useCallback(async (files: FileList | null) => {
        if (!files || files.length === 0 || !pendencia) return;

        const MAX_FILE_BYTES = 50 * 1024 * 1024;
        const erros: string[] = [];
        const arquivosValidos: File[] = [];

        for (const file of Array.from(files)) {
            if (file.size > MAX_FILE_BYTES) {
                erros.push(`Arquivo "${truncarNomeArquivo(file.name)}" excede 50MB.`);
            } else {
                arquivosValidos.push(file);
            }
        }

        if (erros.length > 0) {
            erros.forEach(msg => toast.error(msg, toastDefsError));
        }

        if (arquivosValidos.length > 0) {
            setIsUploading(true);
            try {
                for (const file of arquivosValidos) {
                    const formData = new FormData();
                    formData.append('arquivo', file);

                    try {
                        const response = await api.post(`proposta/${pendencia.id_proposta}/anexos`, formData, {
                            onUploadProgress: (progressEvent: any) => {
                                if (progressEvent.total) {
                                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                }
                            }
                        });
                        
                        if (response.data.status !== 'success') {
                            throw new Error(response.data.message || 'Erro ao enviar arquivo');
                        }
                        
                        const arquivoData = response.data.result || {};
                        
                        const novoArquivo: ArquivoUpload = {
                            id_proposta_arquivo: arquivoData.id_proposta_arquivo,
                            caminho: arquivoData.caminho || file.name,
                            arquivo_final: arquivoData.arquivo_final || 'não',
                            nome: file.name
                        };

                        setArquivos(prev => [...prev, novoArquivo]);
                    } catch (error: any) {
                        const errorMessage = error.response?.data?.message || error.message || `Falha ao enviar "${truncarNomeArquivo(file.name)}"`;
                        toast.error(truncarNomeEmMensagemErro(errorMessage), toastDefsError);
                    }
                }
            } finally {
                setIsUploading(false);
            }
        }
    }, [pendencia]);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileUpload(e.target.files);
        if (e.target) {
            e.target.value = '';
        }
    }, [handleFileUpload]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        handleFileUpload(e.dataTransfer.files);
    }, [handleFileUpload]);

    const removerArquivo = useCallback(async (index: number) => {
        const arquivo = arquivos[index];
        
        if (!arquivo.id_proposta_arquivo || !pendencia) {
            setArquivos(prev => prev.filter((_, i) => i !== index));
            return;
        }

        try {
            await api.delete(`proposta/${pendencia.id_proposta}/anexos/${arquivo.id_proposta_arquivo}`);
            setArquivos(prev => prev.filter((_, i) => i !== index));
            toast.success('Arquivo removido com sucesso', toastDefs);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover arquivo';
            toast.error(truncarNomeEmMensagemErro(errorMessage), toastDefsError);
        }
    }, [arquivos, pendencia]);

    const resetForm = useCallback(() => {
        setResposta('');
        setResultado(null);
        setJustificativa('');
        setArquivos([]);
        setIsDragging(false);
    }, []);

    const popularComDadosAnteriores = useCallback(() => {
        if (!pendencia) return;

        if (pendencia.resposta) {
            if (isFinanceira && pendencia.resultado === 'Recusada') {
                setJustificativa(pendencia.resposta);
            } else {
                setResposta(pendencia.resposta);
            }
        }

        if (isFinanceira && pendencia.resultado) {
            setResultado(pendencia.resultado as 'Aceita' | 'Recusada');
        }

        if (pendencia.arquivos && pendencia.arquivos.length > 0) {
            const arquivosAnteriores: ArquivoUpload[] = pendencia.arquivos
                .filter(arq => !arq.excluido)
                .map(arq => ({
                    id_proposta_arquivo: arq.id_proposta_arquivo,
                    caminho: arq.caminho,
                    arquivo_final: 'não' as const,
                    nome: arq.caminho ? arq.caminho.split('/').pop() || '' : ''
                }));
            setArquivos(arquivosAnteriores);
        }
    }, [pendencia, isFinanceira]);

    useEffect(() => {
        isMountedRef.current = true;
        
        if (modalAtivo && pendencia) {
            popularComDadosAnteriores();
        }
        
        if (!modalAtivo) {
            resetForm();
        }
        
        return () => {
            isMountedRef.current = false;
        };
    }, [modalAtivo, resetForm, popularComDadosAnteriores]);

    const handleConcluir = useCallback(async () => {
        if (!pendencia) return;

        let resultadoBackend: 'resolvida' | 'aceita' | 'recusada' = 'resolvida';

        if (isFinanceira) {
            if (!resultado) {
                toast.error('Selecione Aceitar ou Recusar', toastDefsError);
                return;
            }
            resultadoBackend = resultado === 'Aceita' ? 'aceita' : 'recusada';
            if (resultado === 'Recusada' && !justificativa.trim()) {
                toast.error('Preencha a justificativa', toastDefsError);
                return;
            }
        } else if (isDocumentacao) {
            if (!resposta.trim()) {
                toast.error('Preencha as informações sobre a documentação', toastDefsError);
                return;
            }
            if (arquivos.length === 0) {
                toast.error('Faça upload de pelo menos um arquivo', toastDefsError);
                return;
            }
        } else if (isInformativa) {
            if (!resposta.trim()) {
                toast.error('Preencha a informação solicitada', toastDefsError);
                return;
            }
        }

        try {
            setIsConcluindo(true);
            const payload: any = {
                resultado: resultadoBackend
            };

            if (isFinanceira) {
                payload.aceitar = resultado === 'Aceita' ? 'sim' : 'nao';
                if (resultado === 'Recusada' && justificativa.trim()) {
                    payload.resposta = justificativa.trim();
                }
            } else {
                payload.resposta = resposta.trim();
                if (isDocumentacao && arquivos.length > 0) {
                    payload.arquivos = arquivos;
                }
            }

            const response = await api.put(`/pendencias/${pendencia.id_pendencia}/concluir`, payload);

            if (!isMountedRef.current) return;

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Erro ao concluir pendência');
            }

            if (!isMountedRef.current) return;

            toast.success('Pendência concluída com sucesso', toastDefs);
            resetForm();
            setModalAtivo(false);
            onConcluida();
        } catch (error: any) {
            if (!isMountedRef.current) return;
            let errorMessage = 'Erro ao concluir pendência';
            
            if (error.response?.data) {
                if (error.response.data.erros && Array.isArray(error.response.data.erros) && error.response.data.erros.length > 0) {
                    errorMessage = error.response.data.erros.map((e: any) => e.error || e.message).join(', ');
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(truncarNomeEmMensagemErro(errorMessage), toastDefsError);
            
            // Não fecha o modal em caso de erro para que o usuário possa corrigir
        } finally {
            if (isMountedRef.current) {
                setIsConcluindo(false);
            }
        }
    }, [pendencia, isFinanceira, isDocumentacao, isInformativa, resultado, justificativa, resposta, arquivos, setModalAtivo, onConcluida, resetForm]);

    const handleCancelar = useCallback(() => {
        resetForm();
        setModalAtivo(false);
        onCancelar();
    }, [setModalAtivo, onCancelar, resetForm]);

    if (!pendencia) return null;

    return (
        <ModalDefault
            id={'overlayModalConcluirPendencia'}
            onClose={handleCancelar}
            openModal={modalAtivo}
            width="fit-content"
            maxWidth="600px"
        >
            <ModalContactHead>
                <div>
                    Concluir Pendência
                </div>
                <CloseButtonHeader onClick={handleCancelar}>
                    <IconClose color={comum.colors.primaryRed400} />
                </CloseButtonHeader>
            </ModalContactHead>

            <ModalContentCompact>
                <ModalSection>
                    <Label>Tipo</Label>
                    <Badge variant="tipo">
                        {formatTipo(pendencia.tipo)}
                    </Badge>
                </ModalSection>

                <ModalSection>
                    <Label>Descrição</Label>
                    <Value>{pendencia.descricao}</Value>
                </ModalSection>

                {pendencia.valor && (
                    <ModalSection>
                        <Label>Valor Solicitado</Label>
                        <Value style={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(pendencia.valor)}</Value>
                    </ModalSection>
                )}

                {isFinanceira ? (
                    <>
                        <ModalSection>
                            <Label>Resultado</Label>
                            <ButtonGroup>
                                <Button
                                    variant={resultado === 'Recusada' ? 'outline' : 'secondary'}
                                    onClick={() => setResultado('Recusada')}
                                    style={{ borderColor: resultado === 'Recusada' ? comum.colors.interfaceDanger400 : undefined }}
                                >
                                    Recusar
                                </Button>
                                <Button
                                    variant={resultado === 'Aceita' ? 'primary' : 'secondary'}
                                    onClick={() => setResultado('Aceita')}
                                >
                                    Aceitar
                                </Button>
                            </ButtonGroup>
                        </ModalSection>

                        {resultado === 'Recusada' && (
                            <JustificativaContainer>
                                <ModalSection>
                                    <Label>Justificativa <span className="asterisco">*</span></Label>
                                    <TextArea
                                        placeholder="Informe o motivo da recusa..."
                                        value={justificativa}
                                        onChange={(e) => setJustificativa(e.target.value)}
                                        rows={4}
                                    />
                                </ModalSection>
                            </JustificativaContainer>
                        )}
                    </>
                ) : isDocumentacao ? (
                    <>
                        <ModalSection>
                            <Label>Informações sobre a documentação <span className="asterisco">*</span></Label>
                            <TextArea
                                placeholder="Descreva informações sobre a documentação..."
                                value={resposta}
                                onChange={(e) => setResposta(e.target.value)}
                                rows={4}
                            />
                        </ModalSection>

                        <ModalSection>
                            <Label>Upload de Arquivos <span className="asterisco">*</span></Label>
                            <UploadArea
                                isDragging={isDragging}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <div className="upload-container">
                                    <Upload />
                                </div>
                                <div className="texto-anexos">
                                    <p className="principal">{isDragging ? 'Solte os arquivos aqui' : 'Arraste arquivos para cá'}</p>
                                    <p className="secundario">ou clique para selecionar arquivos</p>
                                </div>
                                <div className="input-container">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileInputChange}
                                        disabled={isUploading}
                                    />
                                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                                        <Upload size={16} />
                                        Selecionar Arquivos
                                    </button>
                                </div>
                            </UploadArea>

                            {arquivos.length > 0 && (
                                <ArquivoList>
                                    {arquivos.map((arquivo, index) => (
                                        <ArquivoItem key={index}>
                                            <div className="file-info">
                                                <FileText size={16} color={comum.colors.interfaceText400} />
                                                <span>{arquivo.nome || arquivo.caminho}</span>
                                            </div>
                                            <div className="remove-btn" onClick={() => removerArquivo(index)}>
                                                <Trash2 size={16} color={comum.colors.interfaceDanger400} />
                                            </div>
                                        </ArquivoItem>
                                    ))}
                                </ArquivoList>
                            )}
                        </ModalSection>
                    </>
                ) : (
                    <ModalSection>
                        <Label>Informação solicitada <span className="asterisco">*</span></Label>
                        <TextArea
                            placeholder="Digite a informação solicitada..."
                            value={resposta}
                            onChange={(e) => setResposta(e.target.value)}
                            rows={5}
                        />
                    </ModalSection>
                )}

                <ButtonGroup>
                    <Button variant="secondary" onClick={handleCancelar} disabled={isConcluindo}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConcluir}
                        disabled={isConcluindo || 
                            (isFinanceira && !resultado) ||
                            (isFinanceira && resultado === 'Recusada' && !justificativa.trim()) ||
                            (isDocumentacao && (!resposta.trim() || arquivos.length === 0)) ||
                            (isInformativa && !resposta.trim())
                        }
                    >
                        {isConcluindo ? 'Concluindo...' : 'Concluir'}
                    </Button>
                </ButtonGroup>
            </ModalContentCompact>
        </ModalDefault>
    );
};

export default ModalConcluirPendencia;

